import CopyButton from "@components/buttons/CopyButton";
import DataPill from "@components/pills/DataPill";
import LabelRow from "@components/rowcomponents/LabelRow";
import { fintracService } from "@services/apis/Fintrac";
import {
  FintracReportTransaction,
  FintracWalletParty,
} from "@services/models/Fintrac/FintracModel";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { formatFintracAmount } from "./fintracChrome";
import { readFintracError } from "./fintracFormat";

type PartyState = {
  loading: boolean;
  error: string;
  parties: FintracWalletParty[];
};

const emptyPartyState = (): PartyState => ({
  loading: false,
  error: "",
  parties: [],
});

const detailCard =
  "flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg";

const shown = (value: string) => (value.trim() ? value : "- -");

const SectionTitle = ({ title }: { title: string }) => (
  <p className="text-text-navbar-active text-[12px] font-semibold underline">
    {title}
  </p>
);

const AddressBlock = ({
  label,
  address,
}: {
  label: string;
  address: string;
}) => (
  <>
    <div className="mb-1 flex flex-row justify-between">
      <span className="text-[12px] font-medium text-text-label">{label}</span>
      {address ? (
        <CopyButton
          copyText={address}
          copyMessage="Address copied to clipboard!"
          fillColor=""
        />
      ) : null}
    </div>
    <span className="mb-2 break-all text-[12px] font-medium text-text-base">
      {address || "- -"}
    </span>
  </>
);

const PartyDetails = ({ party }: { party: FintracWalletParty }) => (
  <>
    <LabelRow label="Type" valueComp={<DataPill value={party.type || "-"} />} />
    <LabelRow
      label="Full name"
      value={shown(party.fullName)}
      isCopy={!!party.fullName.trim()}
    />
    <LabelRow
      label="First name"
      value={shown(party.firstName)}
      isCopy={!!party.firstName.trim()}
    />
    <LabelRow
      label="Last name"
      value={shown(party.lastName)}
      isCopy={!!party.lastName.trim()}
    />
    <LabelRow label="Date of birth" value={shown(party.dateOfBirth)} />
    <LabelRow label="Place of birth" value={shown(party.placeOfBirth)} />
    <LabelRow label="Residence country" value={shown(party.residenceCountry)} />
    <LabelRow
      label="Street"
      value={shown(party.addressStreet)}
      alignment="col"
    />
    <LabelRow label="Building" value={shown(party.addressBuildingNumber)} />
    <LabelRow label="Town" value={shown(party.addressTown)} />
    <LabelRow
      label="Post code"
      value={shown(party.addressPostCode)}
      isCopy={!!party.addressPostCode.trim()}
    />
    <LabelRow label="Address country" value={shown(party.addressCountry)} />
    <LabelRow label="Relationship" value={shown(party.relationship)} />
    <LabelRow
      label="Declared by user"
      value={shown(party.declaredByUserId)}
      isCopy={!!party.declaredByUserId.trim()}
    />
    <LabelRow
      label="External user"
      value={shown(party.externalUserId)}
      isCopy={!!party.externalUserId.trim()}
    />
    <LabelRow label="Created" value={shown(party.createdAt)} />
  </>
);

const PartySection = ({
  title,
  addressLabel,
  address,
  state,
}: {
  title: string;
  addressLabel: string;
  address: string;
  state: PartyState;
}) => (
  <div className={detailCard}>
    <SectionTitle title={title} />
    <AddressBlock label={addressLabel} address={address} />
    {!address ? null : state.loading ? (
      <>
        <Skeleton height={12} width={160} />
        <Skeleton height={12} width={220} />
        <Skeleton height={12} width={180} />
      </>
    ) : state.error ? (
      <span className="text-[12px] font-normal text-text-status-red">
        {state.error}
      </span>
    ) : state.parties.length === 0 ? (
      <span className="text-[12px] font-normal text-text-base">
        No record found for this address
      </span>
    ) : (
      state.parties.map((party, index) => (
        <div key={party.id || `${title}-${index}`} className="space-y-[12px]">
          {index > 0 && <div className="border-b border-border-subtle" />}
          <PartyDetails party={party} />
        </div>
      ))
    )}
  </div>
);

const TransactionPartyPanel = ({
  transaction,
}: {
  transaction: FintracReportTransaction;
}) => {
  const [fromState, setFromState] = useState<PartyState>(emptyPartyState);
  const [toState, setToState] = useState<PartyState>(emptyPartyState);

  useEffect(() => {
    let cancelled = false;
    const fromAddress = transaction.fromAddress?.trim() ?? "";
    const toAddress = transaction.toAddress?.trim() ?? "";

    const loadAddress = async (address: string) => {
      if (!address) return [] as FintracWalletParty[];
      return fintracService.getCounterpartyByWalletAddress(address);
    };

    const apply = async (
      address: string,
      request: Promise<FintracWalletParty[]>,
      setState: (state: PartyState) => void,
    ) => {
      if (!address) {
        setState(emptyPartyState());
        return;
      }
      setState({ loading: true, error: "", parties: [] });
      try {
        const parties = await request;
        if (!cancelled) setState({ loading: false, error: "", parties });
      } catch (error) {
        if (!cancelled) {
          setState({
            loading: false,
            error: readFintracError(error),
            parties: [],
          });
        }
      }
    };

    const fromRequest = loadAddress(fromAddress);
    const toRequest =
      toAddress && toAddress === fromAddress
        ? fromRequest
        : loadAddress(toAddress);
    void apply(fromAddress, fromRequest, setFromState);
    void apply(toAddress, toRequest, setToState);

    return () => {
      cancelled = true;
    };
  }, [transaction.id, transaction.fromAddress, transaction.toAddress]);

  return (
    <div className="mt-[4px] flex flex-col gap-[12px]">
      <div className={detailCard}>
        <div className="flex flex-row justify-between">
          <div className="rounded bg-bg-page-body px-[12px] py-[4px] text-[10px] font-bold text-text-base">
            {transaction.cryptoSymbol || "Transaction"}
          </div>
          <DataPill value={transaction.accountType} />
        </div>
        <LabelRow
          label="Transaction ID"
          value={String(transaction.transactionId)}
          isCopy
        />
        <LabelRow label="User ID" value={String(transaction.userId)} isCopy />
        <LabelRow
          label="Amount"
          value={`${formatFintracAmount(transaction.amountUsd)} USD`}
        />
        <LabelRow
          label="CAD amount"
          value={`${formatFintracAmount(transaction.amountCad)} CAD`}
        />
        <LabelRow label="Rate" value={transaction.usdCadFxRate || "- -"} />
      </div>
      <PartySection
        title="Counterparty"
        addressLabel="From address"
        address={transaction.fromAddress?.trim() ?? ""}
        state={fromState}
      />
      <PartySection
        title="Beneficiary"
        addressLabel="To address"
        address={transaction.toAddress?.trim() ?? ""}
        state={toState}
      />
    </div>
  );
};

export default TransactionPartyPanel;
